import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type InstallState = 'idle' | 'ready' | 'installing' | 'installed' | 'not_supported';

const STORAGE_KEY_PREFIX = 'pandapro_install_shown_';
const PROMPT_DELAY_MS = 3000;

function isMobileDevice(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|mobile|tablet/.test(ua);
}

function isIOS(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function hasDismissed(userId: string): boolean {
  try {
    return localStorage.getItem(getStorageKey(userId)) === 'true';
  } catch {
    return false;
  }
}

function markDismissed(userId: string): void {
  try {
    localStorage.setItem(getStorageKey(userId), 'true');
  } catch {
    // localStorage not available
  }
}

export interface UseInstallPWAReturn {
  /** Whether the install banner should be shown */
  showBanner: boolean;
  /** Is this an iOS device (needs manual instruction) */
  isIOSDevice: boolean;
  /** Current install state */
  installState: InstallState;
  /** Whether device is mobile at all */
  isMobile: boolean;
  /** Trigger native install prompt (Android/Chrome) */
  triggerInstall: () => Promise<void>;
  /** Dismiss banner permanently for this user */
  dismiss: () => void;
  /** Open the install flow from settings (ignores already-shown flag) */
  openInstallFlow: () => void;
  /** Whether iOS instructions modal is open */
  showIOSModal: boolean;
  /** Close iOS modal */
  closeIOSModal: () => void;
}

export const useInstallPWA = (): UseInstallPWAReturn => {
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [installState, setInstallState] = useState<InstallState>('idle');
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  const mobile = isMobileDevice();
  const ios = isIOS();
  const standalone = isInStandaloneMode();

  // Capture the beforeinstallprompt event (Android/Chrome)
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setInstallState('ready');
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Also listen for successful install
    const installedHandler = () => {
      setInstallState('installed');
      setShowBanner(false);
      deferredPromptRef.current = null;
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // Show banner once per user after a short delay
  useEffect(() => {
    if (!user?.id) return;
    if (!mobile) return;
    if (standalone) return; // Already installed — never show
    if (hasDismissed(user.id)) return;

    const timer = setTimeout(() => {
      setShowBanner(true);
    }, PROMPT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user?.id, mobile, standalone]);

  const dismiss = useCallback(() => {
    if (user?.id) markDismissed(user.id);
    setShowBanner(false);
  }, [user?.id]);

  const triggerInstall = useCallback(async () => {
    if (ios) {
      setShowIOSModal(true);
      return;
    }
    if (!deferredPromptRef.current) {
      // Fallback: show iOS-style instructions for unsupported browsers
      setShowIOSModal(true);
      return;
    }
    setInstallState('installing');
    try {
      await deferredPromptRef.current.prompt();
      const { outcome } = await deferredPromptRef.current.userChoice;
      if (outcome === 'accepted') {
        setInstallState('installed');
        setShowBanner(false);
      } else {
        setInstallState('ready');
      }
    } catch {
      setInstallState('ready');
    } finally {
      deferredPromptRef.current = null;
    }
  }, [ios]);

  // Called from Settings — always opens the install flow regardless of dismiss state
  const openInstallFlow = useCallback(() => {
    if (ios) {
      setShowIOSModal(true);
      return;
    }
    if (deferredPromptRef.current) {
      triggerInstall();
    } else {
      // Already installed or not supported — show iOS-like guide
      setShowIOSModal(true);
    }
  }, [ios, triggerInstall]);

  const closeIOSModal = useCallback(() => {
    setShowIOSModal(false);
  }, []);

  return {
    showBanner,
    isIOSDevice: ios,
    installState,
    isMobile: mobile,
    triggerInstall,
    dismiss,
    openInstallFlow,
    showIOSModal,
    closeIOSModal,
  };
};
